import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collectionGroup, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import StudentHomeView from './StudentHomeView';
import StudentClassesView from './StudentClassesView';
import StudentAssignmentsView from './StudentAssignmentsView';
import StudentGradesView from './StudentGradesView';
import StudentTimetableView from './StudentTimetableView';
import StudentOnlineClassesView from './StudentOnlineClassesView';
import StudentEventsView from './StudentEventsView';
import StudentBehaviorView from './StudentBehaviorView';
import StudentNotificationsView from './StudentNotificationsView';
import StudentProfileView from './StudentProfileView';
import StudentStoreView from './StudentStoreView';
import JitsiJoinModal from './JitsiJoinModal';
import { getJitsiRoomName, getPreferredJoinURL } from '../utils/jitsiHelper';
import './StudentDashboard.css';

function StudentDashboard({ studentId, schoolId }) {
  const [student, setStudent] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [jitsiModal, setJitsiModal] = useState({ isOpen: false, roomName: '', displayName: '' });

  const tabs = [
    { id: 0, title: 'Home', icon: '🏠' },
    { id: 1, title: 'My Classes', icon: '🏫' },
    { id: 2, title: 'Assignments', icon: '📝' },
    { id: 3, title: 'Online Classes', icon: '📹' },
    { id: 4, title: 'Events', icon: '🎉' },
    { id: 5, title: 'Timetable', icon: '📅' },
    { id: 6, title: 'Grades', icon: '📊' },
    { id: 7, title: 'Behavior', icon: '📋' },
    { id: 8, title: 'School Store', icon: '🛍️' },
    { id: 9, title: 'Notifications', icon: '🔔' },
    { id: 10, title: 'Profile', icon: '👤' },
  ];

  const loadStudentData = useCallback(async () => {
    try {
      setLoading(true);

      // The full student record lives at classes/{classId}/students/{uuid},
      // keyed by a UUID — NOT the auth uid. Resolve the signed-in account to
      // its roster doc: students/{uid} first (legacy), then via users/{uid}
      // links (studentId / email), preferring nested docs over the top-level
      // students lookup collection.
      let rosterDoc = await getDoc(doc(db, 'students', studentId));

      if (!rosterDoc.exists() || rosterDoc.ref.parent.parent == null) {
        const userSnap = await getDoc(doc(db, 'users', studentId));
        const userData = userSnap.exists() ? userSnap.data() : {};
        let nested = null;

        if (userData.email) {
          const byEmail = await getDocs(query(
            collectionGroup(db, 'students'),
            where('email', '==', userData.email),
            limit(10)
          ));
          nested = byEmail.docs.find(d => d.ref.parent.parent != null) || null;
        }
        if (!nested) {
          const byUser = await getDocs(query(
            collectionGroup(db, 'students'),
            where('userId', '==', studentId),
            limit(10)
          ));
          nested = byUser.docs.find(d => d.ref.parent.parent != null) || null;
        }
        if (!nested && userData.studentId) {
          const bySid = await getDocs(query(
            collectionGroup(db, 'students'),
            where('studentId', '==', userData.studentId),
            limit(10)
          ));
          nested = bySid.docs.find(d => d.ref.parent.parent != null) || null;
        }
        if (nested) rosterDoc = nested;
      }

      if (rosterDoc?.exists()) {
        const data = rosterDoc.data();
        setStudent({
          id: rosterDoc.id,
          ...data,
          classId: data.classId || rosterDoc.ref.parent.parent?.id || null,
          fullName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim()
        });
      }
    } catch (error) {
      console.error('Error loading student data:', error);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadStudentData();
  }, [loadStudentData]);

  const handleJoinClass = (onlineClass) => {
    const joinURL = getPreferredJoinURL(onlineClass);
    if (!joinURL) {
      alert('This class is not joinable at this time.');
      return;
    }

    if (!joinURL.includes('meet.jit.si')) {
      window.open(joinURL, '_blank');
      return;
    }

    const roomName = getJitsiRoomName(onlineClass.id);
    const displayName = student.fullName || 'Student';
    setJitsiModal({ isOpen: true, roomName, displayName });
  };

  const closeJitsiModal = () => {
    setJitsiModal({ isOpen: false, roomName: '', displayName: '' });
  };

  if (loading) {
    return (
      <div className="student-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="student-dashboard">
        <div className="no-student">
          <div className="no-student-icon">❓</div>
          <h3>Student profile not found</h3>
          <p>Please contact your school administrator</p>
        </div>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      <div className="dashboard-layout">
        <nav className="sidebar">
          <div className="sidebar-header">
            <h3>{student.fullName || 'Student'}</h3>
            {student.classId && <p>Class ID: {student.classId}</p>}
          </div>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`nav-button ${selectedTab === tab.id ? 'active' : ''}`}
              onClick={() => setSelectedTab(tab.id)}
            >
              <span className="nav-icon">{tab.icon}</span>
              <span className="nav-label">{tab.title}</span>
            </button>
          ))}
        </nav>

        <main className="main-content">
          {selectedTab === 0 && <StudentHomeView student={student} schoolId={schoolId} onJoinClass={handleJoinClass} />}
          {selectedTab === 1 && <StudentClassesView student={student} schoolId={schoolId} />}
          {selectedTab === 2 && <StudentAssignmentsView student={student} />}
          {selectedTab === 3 && <StudentOnlineClassesView student={student} />}
          {selectedTab === 4 && <StudentEventsView student={student} schoolId={schoolId} />}
          {selectedTab === 5 && <StudentTimetableView student={student} />}
          {selectedTab === 6 && <StudentGradesView student={student} />}
          {selectedTab === 7 && <StudentBehaviorView mode="parent" studentIds={[student.studentId, studentId].filter(Boolean)} />}
          {selectedTab === 8 && <StudentStoreView student={student} schoolId={schoolId} />}
          {selectedTab === 9 && <StudentNotificationsView student={student} />}
          {selectedTab === 10 && <StudentProfileView student={student} schoolId={schoolId} />}
        </main>
      </div>

      <JitsiJoinModal
        isOpen={jitsiModal.isOpen}
        roomName={jitsiModal.roomName}
        displayName={jitsiModal.displayName}
        onClose={closeJitsiModal}
      />
    </div>
  );
}

export default StudentDashboard;
